import { useCallback, useEffect, useState } from "react";
import { DEFAULT_COMPANY_NAME } from "../config";
import { api, type Company, type Direction, type Driver } from "./api";
import { goBack, pushScreen } from "./backstack";
import DriverPicker from "./screens/DriverPicker";
import History from "./screens/History";
import Home from "./screens/Home";
import RegisterFlow from "./screens/RegisterFlow";

type Screen =
  | { name: "home" }
  | { name: "register"; direction: Direction }
  | { name: "history"; company?: Company };

type Toast = { message: string; movementId: number | null };

function loadDriver(): Driver | null {
  try {
    return JSON.parse(localStorage.getItem("driver") ?? "null");
  } catch {
    return null;
  }
}

export default function BoxesApp({ onHome }: { onHome: () => void }) {
  const [driver, setDriver] = useState<Driver | null>(loadDriver);
  const [screen, setScreen] = useState<Screen>({ name: "home" });
  const [companies, setCompanies] = useState<Company[]>([]);
  const [toast, setToast] = useState<Toast | null>(null);

  const refreshCompanies = useCallback(() => {
    api.companies().then(setCompanies).catch(() => {});
  }, []);

  useEffect(refreshCompanies, [refreshCompanies]);

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 10_000);
    return () => clearTimeout(timer);
  }, [toast]);

  const chooseDriver = (picked: Driver) => {
    localStorage.setItem("driver", JSON.stringify(picked));
    setDriver(picked);
    setScreen({ name: "home" });
  };

  if (!driver) return <DriverPicker onPick={chooseDriver} onBack={onHome} />;

  const company =
    companies.find((c) => c.name.toLowerCase() === DEFAULT_COMPANY_NAME.toLowerCase()) ??
    companies[0];

  const openScreen = (next: Screen) => {
    setScreen(next);
    pushScreen(() => setScreen({ name: "home" }));
  };

  const handleRegistered = (message: string, movementId: number) => {
    setToast({ message, movementId });
    refreshCompanies();
    goBack();
  };

  const undo = async () => {
    const id = toast?.movementId;
    setToast(null);
    if (!id) return;
    try {
      await api.undo(id);
      refreshCompanies();
      setToast({ message: "Registration undone", movementId: null });
    } catch (err) {
      setToast({ message: err instanceof Error ? err.message : "Undo failed", movementId: null });
    }
  };

  return (
    <div className="mx-auto flex min-h-dvh max-w-md flex-col px-4 pb-6 pt-4">
      {screen.name === "home" && (
        <Home
          driver={driver}
          company={company}
          onSwitchDriver={() => setDriver(null)}
          onHome={onHome}
          onRegister={(direction) => openScreen({ name: "register", direction })}
          onHistory={() => openScreen({ name: "history" })}
        />
      )}
      {screen.name === "register" && company && (
        <RegisterFlow
          driver={driver}
          direction={screen.direction}
          company={company}
          onDone={handleRegistered}
          onBack={goBack}
        />
      )}
      {screen.name === "history" && (
        <History
          company={screen.company}
          onBack={goBack}
          onMovementDeleted={refreshCompanies}
        />
      )}

      {toast && (
        <div className="pop-in fixed inset-x-4 bottom-5 z-50 mx-auto flex max-w-md items-center justify-between gap-3 rounded-full bg-ink px-5 py-3 text-white shadow-pop">
          <span className="text-sm font-semibold">{toast.message}</span>
          {toast.movementId ? (
            <button
              onClick={undo}
              className="shrink-0 rounded-full bg-lemon px-4 py-1.5 text-sm font-bold text-ink"
            >
              Undo
            </button>
          ) : (
            <button onClick={() => setToast(null)} className="shrink-0 px-2 text-sm font-bold">
              ✕
            </button>
          )}
        </div>
      )}
    </div>
  );
}
